// frontend/src/components/common/ui/ErrorState.tsx
// Unified error state component for consistent error handling across the application

import React from 'react';
import { 
  AlertCircle, 
  RefreshCw, 
  Home, 
  ArrowLeft, 
  Wifi, 
  Server, 
  Shield, 
  FileX,
  AlertTriangle,
  XCircle,
  Info
} from 'lucide-react';

// Utility function to combine class names
const cn = (...classes: (string | undefined | null | boolean)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export type ErrorType = 
  | 'network' 
  | 'server' 
  | 'unauthorized' 
  | 'forbidden' 
  | 'not-found' 
  | 'validation' 
  | 'timeout'
  | 'generic'
  | 'empty-state';

export type ErrorSeverity = 'error' | 'warning' | 'info';

export type ErrorSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ErrorAction {
  label: string;
  action: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  icon?: React.ReactNode;
}

export interface ErrorStateProps {
  type?: ErrorType;
  severity?: ErrorSeverity;
  size?: ErrorSize;
  title?: string;
  message?: string;
  details?: string;
  actions?: ErrorAction[];
  onRetry?: () => void;
  onGoHome?: () => void;
  onGoBack?: () => void;
  showIcon?: boolean;
  className?: string;
  children?: React.ReactNode;
  fullHeight?: boolean;
  centerContent?: boolean;
}

// Error type configurations
const errorConfig = {
  network: {
    icon: Wifi,
    title: 'Network Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    severity: 'error' as ErrorSeverity,
  },
  server: {
    icon: Server,
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    severity: 'error' as ErrorSeverity,
  },
  unauthorized: {
    icon: Shield,
    title: 'Authentication Required',
    message: 'You need to sign in to access this content.',
    severity: 'warning' as ErrorSeverity,
  },
  forbidden: {
    icon: Shield,
    title: 'Access Denied',
    message: 'You don\'t have permission to access this resource.',
    severity: 'error' as ErrorSeverity,
  },
  'not-found': {
    icon: FileX,
    title: 'Page Not Found',
    message: 'The page you\'re looking for doesn\'t exist or has been moved.',
    severity: 'warning' as ErrorSeverity,
  },
  validation: {
    icon: AlertTriangle,
    title: 'Validation Error',
    message: 'Please check your input and try again.',
    severity: 'warning' as ErrorSeverity,
  },
  timeout: {
    icon: AlertCircle,
    title: 'Request Timeout',
    message: 'The request took too long to complete. Please try again.',
    severity: 'warning' as ErrorSeverity,
  },
  generic: {
    icon: XCircle,
    title: 'Error',
    message: 'An unexpected error occurred.',
    severity: 'error' as ErrorSeverity,
  },
  'empty-state': {
    icon: Info,
    title: 'No Data',
    message: 'No data available at the moment.',
    severity: 'info' as ErrorSeverity,
  },
};

// Severity configurations
const severityConfig = {
  error: {
    iconColor: 'text-red-400',
    titleColor: 'text-red-900',
    messageColor: 'text-red-700',
    backgroundColor: 'bg-red-50',
    borderColor: 'border-red-200',
    buttonPrimary: 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white',
    buttonSecondary: 'border-red-300 text-red-700 hover:bg-red-50',
  },
  warning: {
    iconColor: 'text-yellow-400',
    titleColor: 'text-yellow-900',
    messageColor: 'text-yellow-700',
    backgroundColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    buttonPrimary: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500 text-white',
    buttonSecondary: 'border-yellow-300 text-yellow-700 hover:bg-yellow-50',
  },
  info: {
    iconColor: 'text-blue-400',
    titleColor: 'text-blue-900',
    messageColor: 'text-blue-700',
    backgroundColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white',
    buttonSecondary: 'border-blue-300 text-blue-700 hover:bg-blue-50',
  },
};

// Size configurations
const sizeConfig = {
  sm: {
    container: 'p-4',
    icon: 'h-8 w-8',
    title: 'text-lg',
    message: 'text-sm',
    details: 'text-xs',
    button: 'px-3 py-1.5 text-sm',
    spacing: 'space-y-2',
  },
  md: {
    container: 'p-6',
    icon: 'h-10 w-10',
    title: 'text-xl',
    message: 'text-base',
    details: 'text-sm',
    button: 'px-4 py-2 text-sm',
    spacing: 'space-y-3',
  },
  lg: {
    container: 'p-8',
    icon: 'h-12 w-12',
    title: 'text-2xl',
    message: 'text-lg',
    details: 'text-base',
    button: 'px-5 py-2.5 text-base',
    spacing: 'space-y-4',
  },
  xl: {
    container: 'p-12',
    icon: 'h-16 w-16',
    title: 'text-3xl',
    message: 'text-xl',
    details: 'text-lg',
    button: 'px-6 py-3 text-lg',
    spacing: 'space-y-6',
  },
};

const ErrorState: React.FC<ErrorStateProps> = ({
  type = 'generic',
  severity,
  size = 'md',
  title,
  message,
  details,
  actions = [],
  onRetry,
  onGoHome,
  onGoBack,
  showIcon = true,
  className,
  children,
  fullHeight = false,
  centerContent = true,
}) => {
  const config = errorConfig[type];
  const effectiveSeverity = severity || config.severity;
  const severityStyles = severityConfig[effectiveSeverity];
  const sizeStyles = sizeConfig[size];
  
  const effectiveTitle = title || config.title;
  const effectiveMessage = message || config.message;
  const IconComponent = config.icon;

  // Create default actions based on props
  const defaultActions: ErrorAction[] = [];
  
  if (onRetry) {
    defaultActions.push({
      label: 'Try Again',
      action: onRetry,
      variant: 'primary',
      icon: <RefreshCw className="h-4 w-4" />,
    });
  }
  
  if (onGoBack) {
    defaultActions.push({
      label: 'Go Back',
      action: onGoBack,
      variant: 'secondary',
      icon: <ArrowLeft className="h-4 w-4" />,
    });
  }
  
  if (onGoHome) {
    defaultActions.push({
      label: 'Go Home',
      action: onGoHome,
      variant: 'secondary',
      icon: <Home className="h-4 w-4" />,
    });
  }

  const finalActions = actions.length > 0 ? actions : defaultActions;

  const getButtonStyles = (variant: ErrorAction['variant'] = 'primary') => {
    const baseStyles = cn(
      'inline-flex items-center border font-medium rounded-md transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      sizeStyles.button
    );

    switch (variant) {
      case 'primary':
        return cn(baseStyles, severityStyles.buttonPrimary);
      case 'secondary':
        return cn(baseStyles, severityStyles.buttonSecondary, 'bg-white');
      case 'danger':
        return cn(
          baseStyles,
          'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white border-red-600'
        );
      default:
        return cn(baseStyles, severityStyles.buttonPrimary);
    }
  };

  const containerClasses = cn(
    'bg-white rounded-lg shadow-sm border',
    severityStyles.borderColor,
    sizeStyles.container,
    fullHeight && 'min-h-screen flex items-center justify-center',
    className
  );

  const contentClasses = cn(
    centerContent && 'text-center',
    sizeStyles.spacing
  );

  return (
    <div className={containerClasses}>
      <div className={contentClasses}>
        {showIcon && IconComponent && (
          <div className={cn(centerContent && 'flex justify-center')}>
            <IconComponent className={cn(sizeStyles.icon, severityStyles.iconColor)} />
          </div>
        )}
        
        {effectiveTitle && (
          <h3 className={cn(sizeStyles.title, 'font-semibold', severityStyles.titleColor)}>
            {effectiveTitle}
          </h3>
        )}
        
        {effectiveMessage && (
          <p className={cn(sizeStyles.message, severityStyles.messageColor)}>
            {effectiveMessage}
          </p>
        )}
        
        {details && (
          <p className={cn(sizeStyles.details, 'text-gray-500')}>
            {details}
          </p>
        )}
        
        {children && (
          <div className="mt-4">
            {children}
          </div>
        )}
        
        {finalActions.length > 0 && (
          <div className={cn(
            'flex gap-3',
            centerContent ? 'justify-center' : 'justify-start',
            finalActions.length > 2 && 'flex-wrap'
          )}>
            {finalActions.map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                className={getButtonStyles(action.variant)}
              >
                {action.icon && (
                  <span className="mr-2">
                    {action.icon}
                  </span>
                )}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Inline error component for forms and small spaces
interface InlineErrorProps {
  message: string;
  severity?: ErrorSeverity;
  className?: string;
  showIcon?: boolean;
}

export const InlineError: React.FC<InlineErrorProps> = ({
  message,
  severity = 'error',
  className,
  showIcon = true,
}) => {
  const severityStyles = severityConfig[severity];
  
  return (
    <div className={cn('flex items-center space-x-2', className)}>
      {showIcon && (
        <AlertCircle className={cn('h-4 w-4 flex-shrink-0', severityStyles.iconColor)} />
      )}
      <span className={cn('text-sm', severityStyles.messageColor)}>
        {message}
      </span>
    </div>
  );
};

// Error boundary fallback component
interface ErrorBoundaryFallbackProps {
  error: Error;
  resetError: () => void;
  title?: string;
  size?: ErrorSize;
}

export const ErrorBoundaryFallback: React.FC<ErrorBoundaryFallbackProps> = ({
  error,
  resetError,
  title = 'Something went wrong',
  size = 'lg',
}) => {
  return (
    <ErrorState
      type="server"
      size={size}
      title={title}
      message="An unexpected error occurred while rendering this component."
      details={process.env.NODE_ENV === 'development' ? error.message : undefined}
      onRetry={resetError}
      fullHeight
    />
  );
};

// Network error component
interface NetworkErrorProps extends Omit<ErrorStateProps, 'type'> {
  onRefresh?: () => void;
}

export const NetworkError: React.FC<NetworkErrorProps> = ({
  onRefresh,
  ...props
}) => {
  return (
    <ErrorState
      type="network"
      onRetry={onRefresh}
      {...props}
    />
  );
};

// Not found error component
interface NotFoundErrorProps extends Omit<ErrorStateProps, 'type'> {
  resource?: string;
}

export const NotFoundError: React.FC<NotFoundErrorProps> = ({
  resource = 'page',
  title,
  message,
  ...props
}) => {
  return (
    <ErrorState
      type="not-found"
      title={title || `${resource.charAt(0).toUpperCase() + resource.slice(1)} Not Found`}
      message={message || `The ${resource} you're looking for doesn't exist or has been moved.`}
      {...props}
    />
  );
};

// Empty state component
interface EmptyStateProps extends Omit<ErrorStateProps, 'type' | 'severity'> {
  resource?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  resource = 'items',
  title,
  message,
  actionLabel = 'Add New',
  onAction,
  ...props
}) => {
  const actions: ErrorAction[] = [];
  
  if (onAction) {
    actions.push({
      label: actionLabel,
      action: onAction,
      variant: 'primary',
    });
  }

  return (
    <ErrorState
      type="empty-state"
      severity="info"
      title={title || `No ${resource} found`}
      message={message || `You haven't created any ${resource} yet.`}
      actions={actions}
      {...props}
    />
  );
};

// Loading error component
interface LoadingErrorProps extends Omit<ErrorStateProps, 'type'> {
  resource?: string;
}

export const LoadingError: React.FC<LoadingErrorProps> = ({
  resource = 'data',
  title,
  message,
  ...props
}) => {
  return (
    <ErrorState
      type="server"
      title={title || 'Failed to Load'}
      message={message || `Unable to load ${resource}. Please try again.`}
      {...props}
    />
  );
};

export default ErrorState;